import { useTranslation } from 'next-i18next'
import s from '@components/MultiBuyProducts/MultiBuyProducts.module.scss'
import { SimpleProduct, ConfigurableProduct } from '@pylot-data/fwrdschema'
import { MultiBuyProductCard } from 'stores/ooka_usa_store/components/MultiBuyProducts/MultiBuyProductCard'
import React, { useState, useEffect } from 'react'
import { formatPrice } from '@corratech/pylot-price/src/utils/formatPrice'
import { useRouter } from 'next/router'
import { Button } from '@corratech/pylot-ui'
import cn from 'classnames'
import formStyles from '@commonStyles/Forms.module.scss'
import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus.d'
import { useIsMobile } from '@components/common/IsMobile/useIsMobile'
import Slider from 'react-slick'
import type { Settings as ReactSlickSettings } from 'react-slick'
import { SlickprevCircle, SlicknextCircle } from '@components/icons'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import { CartData } from '@corratech/pylot-cart-manager'
import { useAddToCart } from 'stores/ooka_usa_store/components/alfakher/pylot-cart-manager/src/use-add-to-cart'
import type { ProductInterface as PylotProductInterface } from '@pylot-data/pylotschema'
import type { ProductInterface as FwrdProductInterface } from '@pylot-data/fwrdschema'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'

type RelatedProductType = FwrdProductInterface | PylotProductInterface

const settings = {
    arrows: true,
    speed: 400,
    slidesToScroll: 4,
    slidesToShow: 4,
    draggable: true,
    infinite: false,
    dots: false,
    fade: false,
    prevArrow: (
        <button type="button" className="slick-prev">
            <SlickprevCircle />
        </button>
    ),
    nextArrow: (
        <button type="button" className="slick-next">
            <SlicknextCircle />
        </button>
    ),
    responsive: [
        {
            breakpoint: 1023,
            settings: {
                slidesToShow: 3
            }
        },
        {
            breakpoint: 767,
            settings: {
                slidesToShow: 2,
                dots: true,
                arrows: false
            }
        }
    ]
} as ReactSlickSettings

type AddToCartItems = {
    sku: string
    parent_sku?: string
    selected_options?: string[]
    quantity: number
    subscription_end_cycle?: number
    subscription_start_date?: string | null
    subscription_end_date?: string | null
    end_type?: string | null
    billing_period?: string | null
}[]

type MultiBuyProductsProps = {
    product: SimpleProduct | ConfigurableProduct | PylotProductInterface
    showAddToCartErrorMessage: (response: CartData | undefined) => void
    title: string
    sliderSettings: ReactSlickSettings
    gtmLocation?: string
}

export const MultiBuyProducts = ({
    product,
    showAddToCartErrorMessage,
    title,
    sliderSettings,
    gtmLocation
}: MultiBuyProductsProps) => {
    const { t } = useTranslation('common')
    const { locale = 'en' } = useRouter()
    const isMobile = useIsMobile()
    const { addToCart, isAdding } = useAddToCart()
    // @ts-ignore
    const { setGTMLocation } = useAlfakherContext()
    const {
        quantity,
        variant,
        buttonLabel: mainButtonLabel,
        isButtonDisabled,
        selectedOptionUIDs,
        selectedOptions,
        options,
        setSelectedOptions
    } = useProductUI(product)

    // @ts-ignore
    const relatedProductsRaw =
        ((product as unknown) as {
            related_products?: Array<RelatedProductType | null> | null
        })?.related_products ?? []

    const relatedProduct: RelatedProductType[] = relatedProductsRaw.filter(
        (
            rProducts: RelatedProductType | null
        ): rProducts is RelatedProductType =>
            !!rProducts &&
            rProducts?.stock_status === ProductStockStatus.InStock
    )
    // updating price/thumbnail for main product with variant
    const updatedProduct = {
        ...product,
        price_range: variant.price_range,
        thumbnail: variant.thumbnail
    }

    const {
        price_range: {
            minimum_price: {
                final_price: { currency: mainCurrency }
            }
        }
    } = updatedProduct

    const initialSelectedProductSkus = [
        updatedProduct?.sku,
        ...(relatedProduct?.map(
            (product: RelatedProductType) => product?.sku
        ) || [])
    ]
    const [selectedProduct, setSelectedProduct] = useState(
        initialSelectedProductSkus
    )

    useEffect(() => {
        setSelectedProduct(initialSelectedProductSkus)
    }, [product])

    if (
        !relatedProduct?.length ||
        !title ||
        updatedProduct.stock_status === ProductStockStatus.OutOfStock
    )
        return null

    const getProductPrice = () => {
        let totalPrice = relatedProduct.reduce(
            (acc: number, _product: RelatedProductType) => {
                if (selectedProduct.includes(_product?.sku)) {
                    return (
                        acc +
                        (_product?.price_range?.minimum_price?.final_price
                            ?.value ?? 0)
                    )
                }
                return acc
            },
            0
        )

        if (selectedProduct.includes(updatedProduct?.sku)) {
            totalPrice =
                totalPrice +
                (updatedProduct?.price_range?.minimum_price?.final_price
                    ?.value || 0)
        }

        return formatPrice(
            { value: totalPrice, currency: mainCurrency },
            locale
        )
    }

    const addProductToSelected = (productSelect: PylotProductInterface) => {
        const sku = productSelect?.sku
        if (selectedProduct.includes(sku)) {
            setSelectedProduct([...selectedProduct.filter((e) => e !== sku)])
        } else {
            selectedProduct.push(sku)
            setSelectedProduct([...selectedProduct])
        }
    }

    const addProductHandler = async () => {
        setGTMLocation(gtmLocation)
        let addToCartItems: AddToCartItems = [
            {
                sku: product.sku ?? '',
                selected_options: selectedOptionUIDs,
                quantity
            }
        ]

        const isParentProduct = selectedProduct.includes(product.sku)
        if (!isParentProduct) addToCartItems = []
        selectedProduct.forEach((_sku) => {
            if (_sku !== product.sku) {
                addToCartItems.push({
                    sku: _sku as string,
                    parent_sku: product.sku ?? '',
                    quantity: 1
                })
            }
        })

        // 'variant' of a SimpleProduct is the same as 'product'
        const response = await addToCart(
            addToCartItems,
            [product as PylotProductInterface],
            []
        )

        showAddToCartErrorMessage(response)
    }

    const processedIsDisabled =
        !!isAdding ||
        (isButtonDisabled && selectedProduct.includes(updatedProduct?.sku))
    const getButtonLabel = () => {
        if (isAdding) return t('Adding...')
        if (processedIsDisabled) return mainButtonLabel
        return t('Add to Bag')
    }

    const productsListClasses = `products-list-${relatedProduct.length.toString()}`

    return (
        <div
            className={cn(
                s['related-products-wrapper'],
                'related-products-wrapper'
            )}
        >
            <div className={cn(s['related-products'], formStyles.root)}>
                <div className={s['heading-container']}>
                    <h3 className={s['heading-title']}>{title}</h3>
                </div>
                <div
                    className={cn(
                        s[isMobile ? 'products-list-mobile' : 'products-list'],
                        s[productsListClasses]
                    )}
                >
                    <div className={s['product-container']}>
                        <Slider {...sliderSettings}>
                            <MultiBuyProductCard
                                key={product?.sku}
                                product={product as PylotProductInterface}
                                selected={false}
                                isMobile={false}
                                selectedOptions={selectedOptions}
                                options={options}
                                setSelectedOptions={setSelectedOptions}
                                isPdpProduct
                            />
                            {relatedProduct.map(
                                (
                                    rProduct: RelatedProductType,
                                    index: number
                                ) => {
                                    return (
                                        <MultiBuyProductCard
                                            key={index + (rProduct?.sku ?? '')}
                                            onClick={() =>
                                                addProductToSelected(
                                                    (rProduct as unknown) as PylotProductInterface
                                                )
                                            }
                                            product={
                                                (rProduct as unknown) as PylotProductInterface
                                            }
                                            selected={selectedProduct.includes(
                                                rProduct?.sku as never
                                            )}
                                            isMobile={isMobile}
                                            selectedOptions={selectedOptions}
                                            options={options}
                                            setSelectedOptions={
                                                setSelectedOptions
                                            }
                                        />
                                    )
                                }
                            )}
                        </Slider>
                    </div>
                    {!!selectedProduct.length && (
                        <div className={s['action-container']}>
                            <p className={s['buy-all-product']}>
                                {selectedProduct.length > 1
                                    ? t(`Buy all {{items}}`, {
                                          items: selectedProduct.length
                                      })
                                    : t(`Buy 1 item`)}
                            </p>
                            <p className={s['total-price']}>
                                {t(`Total: {{price}}`, {
                                    price: getProductPrice()
                                })}
                            </p>
                            <Button
                                onClick={addProductHandler}
                                variant="primary"
                                disabled={processedIsDisabled}
                                className={cn(
                                    s['multibuy-add-to-cart'],
                                    'btn-sm'
                                )}
                            >
                                {getButtonLabel()}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default MultiBuyProducts
